import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Loader from '../../common/loader/Loader'
import EnrollmentActionButtons from '../../enrollments-billing/components/EnrollmentActionButtons'
import EnrollmentActionModals from '../../enrollments-billing/components/EnrollmentActionModals'
import EnrollmentDetailsCard from '../../enrollments-billing/components/EnrollmentDetailsCard'
import TransferModals from '../../enrollments-billing/components/TransferModals'
import useEnrollmentActions from '../../enrollments-billing/hooks/useEnrollmentActions'
import { requestGetEnrollment } from '../../../actions/enrollmentsBillingActions'
import { deserialize } from '../../../utility/serialization'
import { wasPaidOnline } from '../../../utility/enrollmentsBilling'

import { ReactComponent as BackIcon } from '../../../assets/images/common/icons/back-arrow-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'

import classes from './MemberEnrollmentDetail.module.scss'

// One enrollment, drilled into from the Members drawer's Enrollments tab. The details, the action
// row and the action modals are all the billing feature's own components, driven by the same
// useEnrollmentActions hook the billing drawer uses — so drop/refund, transfer and delete run right
// here, with one implementation of those flows rather than a copy per surface.
//
// The list card carries only the index payload, so re-fetch the full enrollment here for the
// activity timeline, the family-admin email and the invoice the refund modal reads.
const MemberEnrollmentDetail = ({ enrollment: listEnrollment, student, onBack }) => {
  const { t } = useTranslation()
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [enrollment, setEnrollment] = useState(null)

  // Actions re-fetch after they succeed, which can land after the coach has navigated away.
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const loadEnrollment = useCallback(() => (
    requestGetEnrollment({ communityId, enrollmentId: listEnrollment.id })
      .then(({ data }) => mounted.current && setEnrollment(deserialize(data).enrollment))
      // Fall back to the list payload: it lacks the timeline, but the details still render. On a
      // failed refresh after an action, keep what's already loaded rather than reverting to it.
      .catch(() => mounted.current && setEnrollment(current => current || listEnrollment))
  ), [communityId, listEnrollment])

  useEffect(() => {
    loadEnrollment()
  }, [loadEnrollment])

  const { openDrop, openDelete, openTransfer, actionModalProps, transferModalProps } = useEnrollmentActions({
    listEnrollment,
    enrollment,
    loadEnrollment,
    // The enrollment is gone once it's deleted, so return to the member's enrollment list, which
    // re-fetches as it remounts.
    onRemoved: onBack,
  })

  const shown = enrollment || listEnrollment
  const course = shown.course

  return (
    <div className={classes.detail}>
      <button type='button' className={classes.backRow} onClick={onBack}>
        <BackIcon className={classes.backIcon} />
        <span className={classes.studentName}>{student.firstName} {student.lastName}</span>
      </button>

      <section className={classes.programCard}>
        <span className={classes.programName}>{course?.name}</span>
        {course?.startDate && (
          <span className={classes.metaRow}>
            <CalendarIcon className={classes.metaIcon} />
            <span>
              {moment(course.startDate).format('MMM D, YYYY')}
              {course.endDate && ` - ${moment(course.endDate).format('MMM D, YYYY')}`}
            </span>
          </span>
        )}
        {(shown.golfersCount > 0 || course?.meetingTimes) && (
          <span className={classes.metaRow}>
            {shown.golfersCount > 0 && (
              <>
                <GolferIcon className={classes.metaIcon} />
                <span className={classes.golferCount}>{shown.golfersCount}</span>
              </>
            )}
            {course?.meetingTimes && <span>{course.meetingTimes}</span>}
          </span>
        )}
      </section>

      {/* Held back until the full record lands: which drop modal opens, and the invoice the refund
          modal reads, both depend on it - the list payload alone would offer a plain drop for an
          enrollment that has money to refund. */}
      {enrollment ? (
        <>
          <EnrollmentActionButtons
            displayStatus={enrollment.displayStatus}
            invoice={enrollment.invoice}
            paidOnline={wasPaidOnline(enrollment)}
            onDrop={openDrop}
            onRemove={openDelete}
            onTransfer={openTransfer}
          />
          <EnrollmentDetailsCard enrollment={enrollment} displayStatus={enrollment.displayStatus} />
        </>
      ) : (
        <Loader>{t('members.enrollmentsTab.loadingDetails')}</Loader>
      )}

      <EnrollmentActionModals {...actionModalProps} />
      <TransferModals {...transferModalProps} />
    </div>
  )
}

MemberEnrollmentDetail.propTypes = {
  enrollment: PropTypes.object.isRequired,
  student: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
}

export default MemberEnrollmentDetail
